import { cn, formatNumber } from '@worldcoo/ui/lib/utils';
import { DonationsIcon } from './assets/donations-icon';
import { CollectedIcon } from './assets/collected-icon';
import { EmptyChar } from './empty-char';
import { useTranslation } from '@/lib/i18n';
import { useLingui } from '@lingui/react/macro';
import { usePortalCampaignQuery, usePortalMetadataQuery, usePortalCampaignAggregateQuery } from '@/lib/data-hooks';
import { PortalAnimateNumber } from './portal-animate-number';
import { useClient } from '@worldcoo/ui/hooks/use-client';
import { useDebounce } from '@worldcoo/ui/hooks/use-debounce';
import { isNotFound } from '@tanstack/react-router';
import { RingProgressBar } from './ring-progress-bar';

export function CampaignStatistics(props: {
	campaignId: string;
	textEffectDisabled?: boolean;
	variant?: 'sm';
	/**
	 * Adds a "Funded" column next to the goal, shown below `lg` only. On mobile the
	 * sidebar progress card is hidden, so this is where the percentage lives.
	 */
	showFunded?: boolean;
}) {
	const { t } = useLingui();
	const metadata = usePortalMetadataQuery();
	const aggregates = usePortalCampaignAggregateQuery(props.campaignId);
	// Awaited here rather than in each counter below. Every figure in this block needs it, so a boundary
	// per counter bought nothing: each one became its own streamed Suspense boundary, and a boundary React
	// client-renders instead of hydrating leaves its holding container (`<div hidden id="S:n">`) in the
	// document with a stale copy of the subtree — duplicating every counter's `data-testid`. Resolving the
	// data once, above the counters, is both fewer boundaries and fewer duplicate query subscriptions.
	const campaign = usePortalCampaignQuery(props.campaignId);

	// Hide component when metadata, the campaign or aggregates are unavailable
	if (isNotFound(metadata) || isNotFound(campaign)) return null;
	if (!aggregates || !('aggregates' in aggregates)) {
		return null;
	}

	if (metadata.hideStatsOnActiveCampaigns && !aggregates.aggregates[props.campaignId]?.finished) {
		return null;
	}

	return (
		<>
			{/*
			 * `translate="no"` on the whole statistics block, not just on the figures inside it.
			 *
			 * Measured, not assumed (TEC-3703): with a page translator rewriting text nodes before
			 * hydration, this block is the ONLY part of the campaign page whose divergence React
			 * reports as #418. Rewriting ordinary prose — the campaign title, its description, the
			 * "Donate" and "Back to home" labels — produced no report at all, because those sit in
			 * subtrees the router client-renders rather than hydrates. Protecting the numbers alone
			 * was therefore not enough: the terse labels beside them ("Donations", "Funded",
			 * "Goal of …") kept mismatching on their own.
			 *
			 * Protecting the block rather than each label also covers the goal figure, which lives
			 * INSIDE its translated message (`Goal of {goal}`) and so cannot be marked separately
			 * without splitting that message and invalidating its id in every catalog.
			 *
			 * The cost is deliberately contained: only this metrics strip stops being
			 * browser-translatable. All real content — titles, descriptions, NGO names, buttons,
			 * legal copy — stays translatable, which is what a visitor whose language the portal
			 * does not ship actually needs. These labels are already translated by our own catalogs
			 * for every locale the portal does ship, and they sit next to self-evident numbers.
			 */}
			<div
				translate="no"
				className={cn(
					'bg-background flex gap-6 sm:gap-10 lg:gap-16 justify-between lg:justify-start',
					metadata.hideCollected && 'justify-center',
					props.variant === 'sm' && 'gap-16',
				)}
			>
				<div className="flex flex-col font-medium">
					<DonationsIcon className={cn('text-primary mb-2', props.variant === 'sm' && 'size-6')} />
					<div className={cn('whitespace-nowrap text-xl xl:text-2xl', props.variant === 'sm' && 'text-xl xl:text-xl')}>
						<CampaignDonations
							campaignId={props.campaignId}
							textEffectDisabled={props.textEffectDisabled}
							isPageLoaded={campaign.isPageLoaded}
						/>
					</div>
					<span
						className={cn(
							'text-primary text-nowrap text-sm font-semibold lg:text-base',
							props.variant === 'sm' && 'text-sm lg:text-sm',
						)}
					>
						{t`Donations`}
					</span>
				</div>

				{!metadata.hideCollected && (
					<div className="flex flex-col font-medium">
						<CollectedIcon className={cn('text-primary mb-2', props.variant === 'sm' && 'size-6')} />
						<div
							className={cn('whitespace-nowrap text-xl xl:text-2xl', props.variant === 'sm' && 'text-xl xl:text-xl')}
						>
							<CampaignCollected
								campaignId={props.campaignId}
								textEffectDisabled={props.textEffectDisabled}
								isPageLoaded={campaign.isPageLoaded}
							/>
						</div>
						<div
							className={cn(
								// Left to wrap below `lg`: a large goal ("Objetivo de 1.500.000 €") would
								// otherwise force the row past the viewport once the funded column is added.
								'text-primary text-sm font-semibold lg:text-nowrap lg:text-base',
								props.variant === 'sm' && 'text-nowrap text-sm lg:text-sm',
							)}
						>
							<CampaignCollectedFromBudget campaignId={props.campaignId} />
						</div>
					</div>
				)}

				{props.showFunded && <CampaignFunded campaignId={props.campaignId} isPageLoaded={campaign.isPageLoaded} />}
			</div>
		</>
	);
}

function CampaignFunded(props: { campaignId: string; isPageLoaded: boolean }) {
	const { t } = useLingui();
	const { language } = useTranslation();
	const aggregates = usePortalCampaignAggregateQuery(props.campaignId);

	const campaignAggregate =
		aggregates && 'aggregates' in aggregates ? aggregates.aggregates[props.campaignId] : undefined;
	// Pass the real figure: `AnimateNumber` owns the first-paint guard, and without `instant` it starts
	// at zero on both sides and counts up after mount.
	const progress = useDebounce(campaignAggregate?.funding ? campaignAggregate.progress : 0, 150);

	// Mirrors CampaignProgress: without a funding target there is no percentage to show. Truthy, not
	// `== null`: a 0 funding is "no goal" too, and pairing its 0% ring with the "Collected" label beside
	// it was part of TEC-3639.
	if (!campaignAggregate?.funding) return null;

	// Keyed on first visit only, exactly like donations/collected above — see CampaignProgress. Note this
	// deliberately ignores `textEffectDisabled`: that opts a card's NUMBERS out of the count-up, and the
	// ring has no equivalent opt-out, so honouring it here would desync the pair.
	const instant = props.isPageLoaded;

	return (
		<div className="flex flex-col font-medium lg:hidden">
			<RingProgressBar
				radius={40}
				stroke={6}
				className="mb-2 size-8"
				inset={false}
				progress={Math.min(progress, 100)}
				strokeColor="var(--primary)"
				color="var(--primary)"
				entrance={!instant}
			/>
			<div className="text-xl whitespace-nowrap">
				<PortalAnimateNumber locale={language} value={progress} suffix="%" instant={instant} />
			</div>
			<span className="text-primary text-sm font-semibold text-nowrap">{t`Funded`}</span>
		</div>
	);
}

function CampaignDonations(props: { campaignId: string; textEffectDisabled?: boolean; isPageLoaded: boolean }) {
	const { language } = useTranslation();
	const aggregates = usePortalCampaignAggregateQuery(props.campaignId);
	const isClient = useClient();

	const donors = aggregates && 'aggregates' in aggregates ? aggregates.aggregates[props.campaignId]?.donors : undefined;

	// An aggregate entry that never arrives is not zero donations. Once mounted, show the placeholder
	// rather than a figure we do not have.
	if (isClient && donors == null) return <EmptyChar />;

	return (
		<PortalAnimateNumber
			testId="donations"
			locale={language}
			value={donors ?? 0}
			instant={props.textEffectDisabled || props.isPageLoaded}
		/>
	);
}

function CampaignCollected(props: { campaignId: string; textEffectDisabled?: boolean; isPageLoaded: boolean }) {
	const { language } = useTranslation();
	const aggregates = usePortalCampaignAggregateQuery(props.campaignId);
	const isClient = useClient();

	const collected =
		aggregates && 'aggregates' in aggregates ? aggregates.aggregates[props.campaignId]?.collected : undefined;

	// See CampaignDonations: a missing aggregate entry is not zero collected.
	if (isClient && collected == null) return <EmptyChar />;

	return (
		<PortalAnimateNumber
			testId="collected"
			locale={language}
			value={collected ?? 0}
			suffix="€"
			decimalDigits={2}
			instant={props.textEffectDisabled || props.isPageLoaded}
		/>
	);
}

function CampaignCollectedFromBudget(props: { campaignId: string }) {
	const { t } = useLingui();
	const { language } = useTranslation();
	const aggregates = usePortalCampaignAggregateQuery(props.campaignId);
	const funding =
		aggregates && 'aggregates' in aggregates ? aggregates.aggregates[props.campaignId]?.funding : undefined;

	// Wrapped in an element rather than returned as a bare string. A page translator does not edit a
	// text node in place, it replaces it with its own `<font>` wrapper — and React, still holding the
	// original node, then fails its next insertBefore/removeChild against a parent that no longer owns
	// it (the DOM-exception patterns listed in `frontend-error-filter.ts`). A wrapper of our own
	// absorbs the rewrite: the translator nests inside this span, so the node React tracks stays put.
	//
	// This label is NOT browser-translatable: it renders inside the `translate="no"` block above, which
	// covers it deliberately — `Goal of {goal}` carries the figure inside its own translated message, so
	// the number cannot be marked without splitting the message and invalidating its catalog id. The
	// span is belt-and-braces for the rewriters that ignore `translate="no"` altogether, which is the
	// whole class TEC-3703 turned out to be.
	if (!funding) return <span>{t`Collected`}</span>;
	const goal = formatNumber(language, funding, 0);
	return <span>{t`Goal of ${goal}` + ' €'}</span>;
}
